/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { PropsWithChildren, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getCalApi } from "@calcom/embed-react";

export function AppShell({ children }: PropsWithChildren) {
  const [programsOpen, setProgramsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    (async function () {
      const cal = await getCalApi({ namespace: "free-trial" });
      cal("ui", {
        hideEventTypeDetails: false,
        layout: "month_view",
        theme: "dark",
        styles: { branding: { brandColor: "#c4a23a" } },
      });
    })();
  }, []);
  return (
    <div className="min-h-dvh text-gray-100">
      <header className="sticky top-0 z-40 bg-neutral-900/80 backdrop-blur border-b border-neutral-800">
        <div className="mx-auto max-w-6xl px-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 font-semibold tracking-wide"
          >
            <div
              role="img"
              aria-label="Valkyrie Robotics Logo"
              className="w-16 h-16 md:w-20 md:h-20 bg-white"
              style={{
                WebkitMaskImage: "url(/media/valkyrie-logo.svg)",
                maskImage: "url(/media/valkyrie-logo.svg)",
                WebkitMaskSize: "cover",
                maskSize: "cover",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
            <span className="text-sm md:text-base">Valkyrie Robotics</span>
          </a>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 text-gray-300 hover:text-white transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          <nav className="hidden md:flex items-center gap-6 text-sm text-gray-300">
            <div
              className="relative inline-block"
              onMouseEnter={() => setProgramsOpen(true)}
              onMouseLeave={() => setProgramsOpen(false)}
            >
              <button
                className="flex items-center gap-1 hover:text-white transition"
                aria-haspopup="true"
                aria-expanded={programsOpen}
                onClick={(e) => {
                  e.preventDefault();
                  setProgramsOpen((v) => !v);
                }}
              >
                Programs
                <svg
                  className="w-3.5 h-3.5 opacity-70"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
                </svg>
              </button>
              <div
                className={`absolute right-0 top-full w-max rounded-md border-2 border-neutral-700 bg-neutral-900 shadow-2xl z-50 ${
                  programsOpen ? "block" : "hidden"
                }`}
                role="menu"
              >
                <a
                  href="/iq"
                  className="block px-3 py-2 hover:bg-neutral-800 rounded-t-md whitespace-nowrap"
                  role="menuitem"
                >
                  <span className="font-medium">IQ</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Elementary and Middle
                  </span>
                </a>
                <a
                  href="/v5"
                  className="block px-3 py-2 hover:bg-neutral-800 whitespace-nowrap"
                  role="menuitem"
                >
                  <span className="font-medium">V5</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Middle and High
                  </span>
                </a>
                <a
                  href="/summer-camp"
                  className="block px-3 py-2 hover:bg-neutral-800 rounded-b-md whitespace-nowrap"
                  role="menuitem"
                >
                  <span className="font-medium">Summer Camp 2026</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Intensive Learning
                  </span>
                </a>
              </div>
            </div>
            <a className="hover:text-white transition" href="/blog">
              Blog
            </a>
            <a className="hover:text-white transition" href="/about">
              About
            </a>
            <a className="hover:text-white transition" href="/contact">
              Contact
            </a>
            <button
              className="rounded-md bg-brand-600 text-neutral-900 font-medium px-3 py-1.5 transition-transform duration-150 ease-out hover:scale-105 active:scale-95 hover:bg-brand-500"
              data-cal-namespace="free-trial"
              data-cal-link="aplakosh/free-trial"
              data-cal-config='{"layout":"month_view"}'
            >
              Try for free
            </button>
          </nav>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-800 bg-neutral-900/95 backdrop-blur">
            <nav className="px-4 py-4 space-y-3">
              <div className="space-y-2">
                <a
                  href="/iq"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="font-medium">VEX IQ</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Elementary and Middle
                  </span>
                </a>
                <a
                  href="/v5"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="font-medium">VEX V5</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Middle and High
                  </span>
                </a>
                <a
                  href="/summer-camp"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="font-medium">Summer Camp 2026</span>
                  <span className="text-gray-400 text-xs ml-2">
                    — Intensive Learning
                  </span>
                </a>
              </div>
              <div className="pt-2 border-t border-neutral-700">
                <a
                  href="/blog"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </a>
                <a
                  href="/about"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </a>
                <a
                  href="/contact"
                  className="block px-3 py-2 text-gray-300 hover:text-white hover:bg-neutral-800 rounded-md transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </a>
                <button
                  data-cal-namespace="free-trial"
                  data-cal-link="aplakosh/free-trial"
                  data-cal-config='{"layout":"month_view"}'
                  className="w-full mt-3 rounded-md bg-brand-600 text-neutral-900 font-medium px-4 py-2 transition-all duration-150 hover:bg-brand-500 active:scale-95"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Try for free
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>
      <main className="bg-neutral-900">{children}</main>
      <footer className="mt-16 border-t border-neutral-800 bg-neutral-900">
        <div className="mx-auto max-w-6xl px-4 py-12">
          {/* Footer Menu */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Resources */}
            <div>
              <h3 className="text-white font-semibold mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/blog"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Programs */}
            <div>
              <h3 className="text-white font-semibold mb-4">Programs</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/iq"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    VEX IQ
                  </a>
                </li>
                <li>
                  <a
                    href="/v5"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    VEX V5
                  </a>
                </li>
                <li>
                  <a
                    href="/summer-camp"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    Summer Camp
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/terms"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="text-gray-400 hover:text-brand-400 transition-colors duration-200"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-neutral-800 pt-8">
            <p className="text-center text-gray-400 text-sm">
              © {new Date().getFullYear()} Valkyrie Robotics, LLC.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
